'use client';

import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from './utils';

function Dialog(props) {
  return <DialogPrimitive.Root data-slot='dialog' {...props} />;
}

const DialogTrigger = React.forwardRef(({ ...props }, ref) => {
  return (
    <DialogPrimitive.Trigger data-slot='dialog-trigger' ref={ref} {...props} />
  );
});
DialogTrigger.displayName = 'DialogTrigger';

function DialogPortal(props) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />;
}

function DialogClose(props) {
  return <DialogPrimitive.Close data-slot='dialog-close' {...props} />;
}

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => {
  return (
    <DialogPrimitive.Overlay
      data-slot='dialog-overlay'
      ref={ref}
      className={cn(
        'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 tw-fixed tw-inset-0 tw-z-[9999] tw-bg-black/60 tw-backdrop-blur-sm',
        className
      )}
      {...props}
    />
  );
});
DialogOverlay.displayName = 'DialogOverlay';

const DialogContent = React.forwardRef(
  ({ className, children, ...props }, ref) => (
    <DialogPortal data-slot='dialog-portal'>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-slot='dialog-content'
        className={cn(
          'data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 tw-fixed tw-left-[50%] tw-top-[50%] tw-z-[9999] tw-grid tw-max-h-[85vh] tw-w-full tw-max-w-lg tw-translate-x-[-50%] tw-translate-y-[-50%] tw-gap-4 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-border tw-bg-background tw-p-6 tw-shadow-2xl tw-duration-200',
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="tw-rounded-xs focus:tw-outline-hidden tw-absolute tw-right-4 tw-top-4 tw-opacity-70 tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-accent data-[state=open]:tw-text-muted-foreground [&_svg:not([class*='size-'])]:tw-size-4 [&_svg]:tw-pointer-events-none [&_svg]:tw-shrink-0">
          <XIcon />
          <span className='tw-sr-only'>Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);
DialogContent.displayName = 'DialogContent';

function DialogHeader({ className, ...props }) {
  return (
    <div
      data-slot='dialog-header'
      className={cn(
        'tw-flex tw-flex-col tw-gap-2 tw-text-center sm:tw-text-left',
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }) {
  return (
    <div
      data-slot='dialog-footer'
      className={cn(
        'tw-flex tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end',
        className
      )}
      {...props}
    />
  );
}

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    data-slot='dialog-title'
    className={cn('tw-text-lg tw-font-semibold tw-leading-none', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    data-slot='dialog-description'
    className={cn('tw-text-sm tw-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
